import { useState, useCallback, useEffect } from 'react';
import { Sun, Moon, Monitor } from 'lucide-react';
import { notify } from '../utils/toast';

export type Theme = 'light' | 'dark' | 'system';

const getInitialTheme = (): Theme => {
  if (typeof window === 'undefined') return 'system';
  const savedTheme = localStorage.getItem('theme');
  return (savedTheme as Theme) || 'system';
};

export const useTheme = () => {
  const [theme, setTheme] = useState<Theme>(getInitialTheme);

  const applyTheme = useCallback((isDark: boolean) => {
    document.documentElement.classList.toggle('dark', isDark);
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleSystemThemeChange = () => {
      if (theme === 'system') {
        applyTheme(mediaQuery.matches);
      }
    };

    if (theme === 'system') {
      applyTheme(mediaQuery.matches);
    } else {
      applyTheme(theme === 'dark');
    }

    mediaQuery.addEventListener('change', handleSystemThemeChange);
    return () => mediaQuery.removeEventListener('change', handleSystemThemeChange);
  }, [theme, applyTheme]);

  const toggleTheme = useCallback(() => {
    const newTheme: Theme = theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    notify.success(`${newTheme.charAt(0).toUpperCase() + newTheme.slice(1)} theme activated`);
  }, [theme]);

  const getThemeIcon = useCallback(() => {
    switch (theme) {
      case 'light':
        return Sun;
      case 'dark':
        return Moon;
      case 'system':
        return Monitor;
    }
  }, [theme]);

  const getThemeText = useCallback(() => {
    return theme.charAt(0).toUpperCase() + theme.slice(1) + ' theme';
  }, [theme]);

  return {
    theme,
    toggleTheme,
    getThemeIcon,
    getThemeText
  };
};